import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search, Users, Wallet, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Ledger() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/ledger").then((r) => setRows(r.data)).catch((e) => { toast.error(apiError(e)); setRows([]); });
  }, []);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) => r.supplier.toLowerCase().includes(t) || r.projects.some((p) => p.toLowerCase().includes(t)));
  }, [rows, q]);

  const grandTotal = useMemo(() => (rows || []).reduce((s, r) => s + r.total_paid, 0), [rows]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Ledger</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Supplier ledger</h1>
          <p className="text-sm text-slate-500 mt-1.5">Everything you&apos;ve paid, grouped by supplier.</p>
        </div>
        <div className="relative flex-1 md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search suppliers…" className="h-12 pl-9" data-testid="ledger-search" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 stagger">
        <SummaryCard icon={<Users />} label="Suppliers" value={String(rows?.length || 0)} />
        <SummaryCard icon={<Wallet />} label="Total paid all-time" value={fmtMoney(grandTotal)} tone="paid" />
        <SummaryCard icon={<Building2 />} label="Payments recorded" value={String((rows || []).reduce((s, r) => s + r.payment_count, 0))} />
      </div>

      {filtered === null ? (
        <div className="h-40 bg-white border border-border rounded-lg animate-pulse" />
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2" data-testid="ledger-empty">
          <Users className="h-8 w-8 text-slate-400 mx-auto mb-3" />
          <h3 className="font-display text-lg font-semibold">No supplier payments yet</h3>
          <p className="text-sm text-slate-500 mt-1">Record payments against your trades and they&apos;ll appear here.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="ledger-list">
          {filtered.map((r) => (
            <Card key={r.supplier} className="border border-border shadow-sm overflow-hidden" data-testid={`ledger-row-${r.supplier}`}>
              <button
                type="button"
                onClick={() => setExpanded((prev) => ({ ...prev, [r.supplier]: !prev[r.supplier] }))}
                className="w-full text-left p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Supplier</div>
                  <div className="font-display text-lg font-semibold text-slate-900 truncate">{r.supplier}</div>
                  <div className="text-xs text-slate-500 truncate mt-0.5">{r.projects.join(" · ") || "—"}</div>
                </div>
                <div className="grid grid-cols-3 md:min-w-[360px] gap-4 md:gap-6">
                  <MiniStat label="Total paid" value={fmtMoney(r.total_paid)} tone="paid" />
                  <MiniStat label="Payments" value={String(r.payment_count)} />
                  <MiniStat label="Projects" value={String(r.projects.length)} />
                </div>
              </button>

              {expanded[r.supplier] && (
                <div className="border-t border-border bg-slate-50/50 divide-y divide-border" data-testid={`ledger-payments-${r.supplier}`}>
                  {r.payments.map((p) => (
                    <div key={p.id} className="p-3 md:p-4 flex flex-col md:flex-row md:items-center gap-2 md:gap-6 text-sm">
                      <div className="font-display font-semibold text-slate-900 md:w-32">{fmtMoney(p.amount, true)}</div>
                      <div className="text-slate-500 md:w-28">{fmtDate(p.payment_date)}</div>
                      <div className="text-slate-600 flex-1 min-w-0">
                        <Link to={`/projects/${p.project_id}`} className="hover:text-brandblue font-medium">{p.project_name}</Link>
                        {" · "}
                        <Link to={`/trades/${p.trade_id}`} className="hover:text-brandblue">{p.trade_name}</Link>
                      </div>
                      {p.invoice_number && <div className="text-xs text-slate-400 uppercase tracking-wider">INV {p.invoice_number}</div>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon, label, value, tone = "neutral" }) {
  const toneCls = { neutral: "text-slate-900", paid: "text-status-paid-text" }[tone];
  const iconCls = { neutral: "bg-slate-100 text-slate-600", paid: "bg-status-paid-bg text-status-paid-text" }[tone];
  return (
    <Card className="p-4 border border-border shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center [&>svg]:h-4 [&>svg]:w-4 ${iconCls}`}>{icon}</div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className={`font-display font-bold text-xl md:text-2xl ${toneCls}`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function MiniStat({ label, value, tone = "neutral" }) {
  const c = { neutral: "text-slate-900", paid: "text-status-paid-text" }[tone];
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`font-display font-semibold text-sm md:text-base ${c}`}>{value}</div>
    </div>
  );
}
